import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { camelize, decamelize } from 'humps'
import { ActionAnchor } from './common/Anchor'
import Divider from './common/Divider'
import { toTitleCase, renderReportType } from '../utils/textFormatters'
import { LOCATION_TYPES, PRESSURE_REPORT_READING_TYPES } from '../constants'

const PRESSURE_COLUMNS = [
  PRESSURE_REPORT_READING_TYPES.PRESSURE,
  PRESSURE_REPORT_READING_TYPES.LOW_PRESSURE,
  PRESSURE_REPORT_READING_TYPES.HIGH_PRESSURE,
]

const ReportCSVDownload = ({
  siteName,
  pages,
  columns,
  reportType,
  pressureReadings,
}) => {
  const handleDownload = () => {
    // only the pressure readings selected for the report (all when not given)
    const pressureColumns = pressureReadings
      ? PRESSURE_COLUMNS.filter(x =>
          pressureReadings.map(y => camelize(y)).includes(x)
        )
      : PRESSURE_COLUMNS
    const reportTypeFormatted = renderReportType(reportType)
    // pressure monitor readings go in columns of their own, with each
    // monitor's units alongside
    const hasPressurePages = pages.some(
      page => page.locationType === LOCATION_TYPES.PRESSURE_MONITOR
    )
    const pressureHeader = hasPressurePages
      ? `,${pressureColumns.map(x => decamelize(x))},pressure_units`
      : ''
    let csv = `Site,Report Type,Page Name,Location,Time,${columns.map(x =>
      x.toString()
    )}${pressureHeader}\n`
    pages.forEach(page => {
      const pageContainsSensorData =
        page.items.filter(x => x.data.length > 0).length > 0

      if (pageContainsSensorData) {
        const pageName = `${
          page.buildingName ? `${page.buildingName} - ` : ''
        }${page.name}${
          page.locationType
            ? ` (${toTitleCase(page.locationType)}${
                page.items.length > 1 ? 's' : ''
              })`
            : ''
        }`

        const isPressurePage =
          page.locationType === LOCATION_TYPES.PRESSURE_MONITOR

        page.items.forEach(x => {
          const units = x.units || page.units || ''

          x.data.forEach(y => {
            const pressureValues = hasPressurePages
              ? `,${
                  isPressurePage
                    ? [...pressureColumns.map(z => y[z]), units]
                    : pressureColumns.map(() => '').concat('')
                }`
              : ''
            const sensorValues = isPressurePage
              ? columns.map(() => '')
              : columns.map(z => y[camelize(z)])

            csv += `${siteName},${reportTypeFormatted},${pageName},${x.label},${y.time},${sensorValues}${pressureValues}\n`
          })
        })
      }
    })

    let fileName = `${siteName} — ${reportTypeFormatted} — ${moment().toISOString()}.pdf`
    const encodedCsv = global.encodeURI(csv)

    const hiddenElement = document.createElement('a')
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodedCsv
    hiddenElement.target = '_blank'
    hiddenElement.download = `${fileName}.csv`
    hiddenElement.click()
  }

  return (
    <section className="ReportCSVDownload">
      <Divider vertical />
      <span>
        <ActionAnchor onClick={handleDownload}>Download CSV</ActionAnchor>
      </span>
    </section>
  )
}

ReportCSVDownload.propTypes = {
  siteName: PropTypes.string.isRequired,
  reportType: PropTypes.string.isRequired,
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  pressureReadings: PropTypes.arrayOf(PropTypes.string),
}

export default ReportCSVDownload
